import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  DropdownElement,
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './pay-per-minute-tab.scss';

// Store entities
import {
  getAvailableCallPrebookedTime,
  updateAvailableCallPrebookedTime
} from '../../../../store/session-settings/session-settings.thunks';

// Models
import { IPrebookedTimes } from '../../../../interfaces/session-settings.interfaces';

export type TPayPerMinuteTabRef = {
  isHasChanges: () => boolean;
  resetState: () => void;

};

type TPayPerMinuteTabProps = {
  booked: IPrebookedTimes;
};

export const PayPerMinuteTab = forwardRef<TPayPerMinuteTabRef, TPayPerMinuteTabProps>(
  ({ booked }: TPayPerMinuteTabProps, ref) => {

    const [form] = Form.useForm<{
      call_prebooked_time: string;
    }>();

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);
    const [endOfSession, setEndOfSession] = useState<string>('');

    const dispatch = useDispatch<any>();

    const timeOptions: { value: string, label: string }[] = booked.available_call_prebooked_time?.map((duration) => ({
      value: `${ duration }`, label: `${ duration } minutes`
    }));

    const onValuesChange = (_: { call_prebooked_time: string }, { call_prebooked_time }: {
      call_prebooked_time: string
    }): void => {
      const hasChanges = call_prebooked_time !== booked.call_prebooked_time.toString();
      const endOfSession = call_prebooked_time === '5' || call_prebooked_time === '10' || call_prebooked_time === '15' ? '3' : '5'
      setEndOfSession(endOfSession);
      setHasChanges(hasChanges);
    }

    const saveChanges = async (): Promise<void> => {
      const { call_prebooked_time } = form.getFieldsValue();
      setSavingChanges(true);

      const { payload } = await dispatch(updateAvailableCallPrebookedTime(call_prebooked_time));

      setSavingChanges(false);
      if (!payload) return;
      setHasChanges(false);
      form.resetFields();
    }

    const loadAvailableCallPrebookedTime = (): void => {
      dispatch(getAvailableCallPrebookedTime());
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldValue('call_prebooked_time', booked.call_prebooked_time.toString());
        setHasChanges(false);
        setSavingChanges(false);
      }
    }));

    useEffect(() => {
      loadAvailableCallPrebookedTime();
    }, []);

    return (
      <div className="pay-per-minute-tab">
        <div className="pay-per-minute-tab--description business--description">
          Settings for Instant Calls charged by the minute. This allows you to offer flexible, real-time
          Consultant-Client interaction billed according to the duration of each call.
        </div>
        <Form className="pay-per-minute-tab--body business--body-items" form={ form } onValuesChange={ onValuesChange }>
          <div className="body-item">
            <div className="body-item--title">
              Prepaid Call Time
            </div>
            <div className="body-item--description">
              Setting for charging Clients upfront for anticipated call duration, with automatic refunds for unused
              time.
            </div>
            <div className="body-item--content">
              { !!booked.call_prebooked_time &&
                <Form.Item name="call_prebooked_time" initialValue={ booked.call_prebooked_time.toString() }>
                  <DropdownElement options={ timeOptions } withoutAll={ true }
                                   placeholder={ 'Booked time' }
                  />
                </Form.Item> }
              { hasChanges && <div className="content-actions">
                <CustomButton size={ 'small' } loading={ savingChanges } text={ 'Save' } onClick={ saveChanges }/>
              </div> }
            </div>
          </div>
          <div className="body-item">
            <div className="body-item--title">
              Call Completion Alert
            </div>
            <div className="body-item--description">
              Automatic call termination notification with the option to continue the call for the next anticipated call
              duration.
            </div>
            <div className="body-item--content">
              { !!booked.call_socket_time && <div className="content-readonly">
                <div className="content-readonly--value">
                  { endOfSession || booked.call_socket_time }
                </div>
                <div className="content-readonly--unit">
                  min
                </div>
              </div> }
            </div>
          </div>
        </Form>
      </div>
    );
  }
)
